import { apiClient } from './client';

/**
 * Interface for stock detail data
 */
export interface StockDetailData {
  stock: {
    isin: string;
    stock_name: string;
    nse_code: string;
    bse_code: string;
    industry_name: string;
    sector_name: string;
  };
  financial_data: {
    current_price: number;
    price_to_earning: number;
    market_capitalization: number;
    promoter_holding: number;
    return_on_equity: number;
    return_on_capital_employed: number;
    debt_to_equity: number;
    eps: number;
    earnings_yield: number;
    return_over_1month: number;
    return_over_3months: number;
    return_over_6months: number;
    return_over_1year: number;
    price_to_book_value: number;
    price_to_sales: number;
    volume_1week_average: number;
    volume_1month_average: number;
    down_from_52w_high: number;
    total_shares?: number;
    free_float_shares?: number;
    free_float_mcap?: number;
  };
  chart_data: Array<{
    date: string;
    close_price: number;
    traded_qty: number;
    delivery_qty: number;
    number_of_trades: number;
    dma_20?: number | null;
    dma_50?: number | null;
    dma_200: number;
    avg30_tq?: number | null;
  }>;
}

/**
 * Interface for the comprehensive stock detail response from the new API
 */
export interface ComprehensiveStockDetail {
  isin: string;
  stock_name: string;
  nse_code?: string;
  bse_code?: string;
  stock_code?: string;
  industry_name?: string;
  sector_name?: string;
  trading_view_url?: string;
  screener_url?: string;
  financial_data?: {
    current_price?: number;
    price_to_earning?: number;
    market_capitalization?: number;
    promoter_holding?: number;
    return_over_1day?: number;
    return_over_1week?: number;
    return_over_1month?: number;
    return_over_3months?: number;
    return_over_6months?: number;
    return_over_1year?: number;
    return_over_3years?: number;
    return_over_5years?: number;
    return_over_10years?: number;
    volume?: number;
    volume_1week_average?: number;
    volume_1month_average?: number;
    yoy_quarterly_sales_growth?: number;
    yoy_quarterly_profit_growth?: number;
    qoq_sales?: number;
    qoq_profits?: number;
    sales_latest_quarter?: number;
    net_profit_latest_quarter?: number;
    sales?: number;
    operating_profit?: number;
    net_profit?: number;
    sales_growth_3years?: number;
    profit_growth_3years?: number;
    return_on_equity?: number;
    number_of_equity_shares?: number;
    number_of_equity_shares_preceding_year?: number;
    return_on_capital_employed?: number;
    pledged_percentage?: number;
    price_to_sales?: number;
    price_to_book_value?: number;
    debt_to_equity?: number;
    down_from_52w_high?: number;
    eps_growth_yoy?: number;
    peg_ratio?: number;
    ebit_ratio?: number;
    eps?: number;
    debt?: number;
    change_in_promoter_holding?: number;
    earnings_yield?: number;
    industry_pe?: number;
    price_to_free_cash_flow?: number;
    evebitda?: number;
    enterprise_value?: number;
    avg_return_on_equity_5years?: number;
    avg_return_on_equity_3years?: number;
    return_on_assets?: number;
    free_float_mcap?: number;
    free_float_shares?: number;
    is_sme?: boolean;
  };
  deals?: Array<{
    client_name?: string;
    deal_type?: string;
    action?: string;
    reported_date?: string;
    average_price?: number;
    quantity?: number;
    traded_percentage?: number;
    last_traded_price?: number;
    post_transaction_holding?: number;
    period?: string;
    security_type?: string;
    mode?: string;
    regulation_type?: string;
  }>;
  surveillances?: Array<{
    surveillance_type?: string;
    stage?: string;
    effective_date?: string;
    exchange?: string;
  }>;
  esm_history?: Array<{
    current_esm_stage?: number;
    transition_date?: string;
    transaction_type?: string;
  }>;
  bhavcopy_data?: Array<{
    date: string;
    open_price?: number;
    high_price?: number;
    low_price?: number;
    close_price?: number;
    volume?: number;
    delivery_qty?: number;
    delivery_percentage?: number;
    number_of_trades?: number;
    exchanges: string[];
  }>;
}

/**
 * Fetches detailed information about a stock using its ISIN
 * @param isin - The ISIN (International Securities Identification Number) of the stock
 * @returns Promise resolving to stock detail data
 */
export const getStockDetail = async (isin: string): Promise<StockDetailData> => {
  try {
    const response = await apiClient.get('market/big-buys-stock-detail', {
      params: { isin }
    });
    
    // Type assertion to handle the unknown type from apiClient
    const responseData = response.data as { data?: { chart_data?: unknown } };
    
    // Access the nested data structure from the API response
    if (!responseData?.data?.chart_data || !Array.isArray(responseData.data.chart_data)) {
      console.warn('Invalid or missing chart_data data structure:', responseData);
      throw new Error('Invalid stock data structure received from server');
    }
    
    return responseData.data as StockDetailData;
  } catch (error) {
    console.error('Error fetching stock details:', error);
    throw error;
  }
};

/**
 * Fetches comprehensive stock details using the new API endpoint
 * @param isin - The ISIN (International Securities Identification Number) of the stock
 * @returns Promise resolving to comprehensive stock detail data
 */
export const getComprehensiveStockDetail = async (isin: string): Promise<ComprehensiveStockDetail> => {
  try {
    const response = await apiClient.get('market/stock-detail/', {
      params: { isin }
    });
    
    // Access the nested data structure from the API response
    const responseData = response.data as { status: string; data: ComprehensiveStockDetail };
    
    if (!responseData?.data) {
      console.warn('Invalid or missing data structure:', responseData);
      throw new Error('Invalid stock data structure received from server');
    }
    
    return responseData.data;
  } catch (error) {
    console.error('Error fetching comprehensive stock details:', error);
    throw error;
  }
}; 